import {
	FIELD_TYPES,
	FIELD_TEMPLATES,
	RELATIONSHIP_TYPES,
	IDENTIFIER_TYPES
} from "constants.js";
import {
	forEach,
	find,
	filter,
	findIndex,
	has,
	uniqWith,
	keyBy,
	toArray,
	map, 
	uniq,
	uniqBy
} from "lodash";

export const UNIQUE_TIMEZONE_ERRORS = {
	MULTIPLE: "Multiple",
	ZERO: "Zero"
};

class EntityManager {
	constructor($userProfile, $logger) {
		this.$userProfile = $userProfile;
		this.$logger = $logger;

		this.reset();
	}

	reset() {
		this.allEntities = {};
		this.entityIdToFieldIdMap = {};
		this.allEntityConfigs = {};
		this.userScopedEntities = {};
		this.userScopedEntityTypes = [];
		this.primaryFieldEntities = null;
		this.selectedEntity = null;
		this.allPrimaries = false;
		this.primaryFieldId = undefined;
		this.primaryEntityQueryConfiguration = undefined;
		this.primaryFieldLabel = "";
		this.displayMetadata = undefined;
	}

	isEntitySelf(entity) {
		return !!entity.isSelf;
	}

	findFriendlyByPrimaryId = (fieldOptions, entityId) => {
		const found = fieldOptions.find(d => d.Id === entityId);
		if(found !== undefined) {
			return found;
		}

		for(const friendly of fieldOptions) {
			if(friendly.Entities !== undefined && friendly.Entities.length > 0) {
				const primaries = friendly.Entities.map(e => e.Entity);
				const foundPrimary = this.findFriendlyByPrimaryId(primaries, entityId);

				if(foundPrimary !== undefined) {
					return friendly;
				}
			}
		}
	}

	selectEntity(entityId, pickFriendlyWithAllPrimaries = false) {
		this.allPrimaries = false;

		this.selectedEntity = this.primaryFieldEntities[entityId];
		if(this.selectedEntity && this.selectedEntity.isFriendly && !this.selectedEntity.isSelf) {
			this.allPrimaries = true;

			if(this.selectedEntity.Entities.length === 1) {
				this.selectedEntity = this.primaryFieldEntities[this.selectedEntity.Entities[0].Entity.Id];
			}
		}
		else if(pickFriendlyWithAllPrimaries && this.selectedEntity && this.selectedEntity.isPrimary) {
			const friendly = this.getFriendlyForPrimaryId(entityId);
			if(friendly) {
				this.selectedEntity = friendly;
				this.allPrimaries = true;
			}
		}	

		const thisUser = this.$userProfile.getUserProfile();
		thisUser.selectedEntityId = entityId;
		this.$userProfile.setUserProfile(thisUser);
	}

	isAllPrimaries() {
		return this.isAllPrimaries;
	}

	getFriendlyForPrimaryId(id) {
		const primary = this.primaryFieldEntities[id];
		if(primary.isSelf) {
			return primary;
		}
		
		const friendlyId = find(primary.friendlyIds, e => has(this.primaryFieldEntities, e));

		return this.primaryFieldEntities[friendlyId];
	}

	getSelectedEntity() {
		if(!this.selectedEntity) {
			const thisUser = this.$userProfile.getUserProfile();

			const selectedEntityId = thisUser && thisUser.selectedEntityId;

			if(selectedEntityId) {
				this.selectEntity(selectedEntityId);
			}
		}

		if(this.selectedEntity) {
			let friendlyId = this.selectedEntity.Id;

			if(this.selectedEntity.isPrimary) {
				friendlyId = find(this.selectedEntity.friendlyIds, e => has(this.primaryFieldEntities, e));
			}

			const returnEntity = {
				primary: this.selectedEntity.isFriendly ? {} : this.selectedEntity,
				friendly: this.primaryFieldEntities[friendlyId]
			};

			return returnEntity;
		}

		return {
			primary: null,
			friendly: null
		};
	}

	getEntity(fieldId, entityId) {
		if(this.allEntities[fieldId]) {
			return this.allEntities[fieldId][entityId];
		}
		else if(fieldId.toString().includes("-")) {
			const idBreakIndex = fieldId.indexOf("-");
			const trueFieldId = parseInt(fieldId.substring(0, idBreakIndex));

			return this.allEntities[trueFieldId][entityId];
		}
	}

	getEntityPairs(fieldId, entityId) {
		const entity = this.getEntity(fieldId, entityId);
		if(entity.isSelf) {
			return [{
				friendly: entity, 
				primary: entity
			}];
		}
		else if(entity.isFriendly) {
			return entity.Entities.map(e => {
				return {
					friendly: entity, 
					primary: e.Entity
				};
			});
		}
		
		const friendliesWithThisPrimary = entity.friendlyIds.map(fId => {
			const friendly = this.getEntity(fieldId, fId);
			
			return {
				friendly,
				primary: entity
			};
		});

		return friendliesWithThisPrimary;
	}

	getUniqueTimezoneForEntity(fieldId, entityId) {
		const all = this.getEntityPairs(fieldId, entityId);

		const allAssociated = all.reduce((acc, next) => {
			const bothEntities = Object.values(next);
			acc.push(...bothEntities);

			return acc;
		}, []);

		const allTimezones = allAssociated
			.map(e => e.TimeZone?.Code)
			.filter(c => c !== undefined);

		const uniqueTimezones = uniq(allTimezones);
		if(uniqueTimezones.length > 1) {
			return UNIQUE_TIMEZONE_ERRORS.MULTIPLE;
		}
		else if(uniqueTimezones.length < 1) {
			return UNIQUE_TIMEZONE_ERRORS.ZERO;
		}

		return uniqueTimezones[0];
	}

	getEntitiesForAllFields(entityId) {
		const allFieldEntities = [];
		forEach(this.allEntities, (fieldEntities) => {
			if(fieldEntities[entityId]) {
				allFieldEntities.push(fieldEntities[entityId]);
			}
		});

		return allFieldEntities;
	}

	getSeason(seasonCode) {
		return this.seasons && this.seasons[seasonCode];
	}

	getClassification(classificationCode) {
		return this.classifications && this.classifications[classificationCode];
	}

	getClassificationById(classificationId) {
		return this.classifications && find(this.classifications, c => c.Id === classificationId);
	}

	getPrimaryFieldEntities() {
		return filter(this.primaryFieldEntities, (entity) => entity.isFriendly);
	}

	getEntityQueryConfigurations() {
		const uniqueTypes = uniqWith(
			map(this.allEntityConfigs, c => [c.Primary, c.Friendly])
				.reduce((a, b) => a.concat(b), []),
			(a, b) => a.Type.Code === b.Type.Code);

		const formTypeConfigs = keyBy(uniqueTypes, c => c.Type.Code);
		return formTypeConfigs;
	}

	updateEntity(newEntity) {
		const fieldIds = this.entityIdToFieldIdMap[newEntity.Id];

		forEach(fieldIds, (id) => {
			const currentEntity = this.getEntity(id, newEntity.Id);

			const updatedEntity = {
				...currentEntity,
				...newEntity
			};

			this.allEntities[id][newEntity.Id] = updatedEntity;
		});
	}

	loadEntityFieldData(form) {
		this.form = form;

		this.loadSeasons();
		this.loadClassifications();

		const entityFields = form.Fields.filter((f) => f.Type.Code === FIELD_TYPES.entity || f.Type.Code === FIELD_TYPES.entityContact);

		const userScopedEntityTypes = [];
		forEach(entityFields, (field) => {
			this.allEntities[field.Id] = this.loadEntityField(field);

			forEach(this.allEntities[field.Id], e => {
				if(this.entityIdToFieldIdMap[e.Id] === undefined) {
					this.entityIdToFieldIdMap[e.Id] = [];
				}

				this.entityIdToFieldIdMap[e.Id].push(field.Id);
			});

			if(field.IsPrimary) {
				this.primaryFieldId = field.Id;
				this.primaryFieldEntities = this.allEntities[field.Id];
				this.primaryEntityQueryConfiguration = field.EntityQueryConfiguration;
				this.primaryFieldLabel = field.Name;
				const typeConfig = this.form.Agency.Types.find(type =>
					type.Code === this.primaryEntityQueryConfiguration?.Friendly?.Type?.Code
				);
				this.displayMetadata = {
					IsCodeHidden: field.IsCodeHidden,
					IsCodeSystemManaged: typeConfig?.IsCodeSystemManaged
				};
			}

			const eqc = field.EntityQueryConfiguration;

			if(eqc.IsUserScoped) {
				const entityArray = toArray(this.allEntities[field.Id]);
				this.userScopedEntities[eqc.Friendly.Type.Code] = this.userScopedEntities[eqc.Friendly.Type.Code] ?? {};
				this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code] = 
					this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code] ?? {};
				this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code][eqc.RelationshipType.Code] = 
					(this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code][eqc.RelationshipType.Code] ?? []).concat(entityArray);
				
				this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code][eqc.RelationshipType.Code] = 
					uniqWith(this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code][eqc.RelationshipType.Code], 
						(next, prev) => next.Id === prev.Id
					);
				userScopedEntityTypes.push(...this.userScopedEntities[eqc.Friendly.Type.Code][eqc.Primary.Type.Code][eqc.RelationshipType.Code].map(e => e.Type));
			}
		});

		const uniqueEntityTypes = uniqBy(userScopedEntityTypes, t => t.Code);
		this.userScopedEntityTypes = keyBy(uniqueEntityTypes, t => t.Code);
	}

	loadSeasons = () => {
		if(!!this.form.Seasons) {
			this.seasons = {};
			this.form.Seasons.map(season => {
				this.seasons[season.Code] = season;
				return;
			});
		}
	}

	loadClassifications = () => {
		if(!!this.form.Agency.Classifications) {
			this.classifications = {};
			this.form.Agency.Classifications.map(classification => {
				this.classifications[classification.Code] = classification;
				return;
			});
		}
	}

	loadEntityField(field) {
		if((field?.Type?.Code === FIELD_TYPES.entity || field?.Type?.Code === FIELD_TYPES.entityContact)
			&& (field.EntityQueryConfiguration && field.EntityQueryConfiguration.RelationshipType && field.EntityQueryConfiguration.RelationshipType.Code === RELATIONSHIP_TYPES.Self)) {

			if(field.Template && field.Template === FIELD_TEMPLATES.EntitySelector) {
				field.Template = FIELD_TEMPLATES.Selection;
			}
			field.isSelf = true;
		}

		const fieldEntities = this.loadEntityListData(field.Data, field.isSelf);

		forEach(fieldEntities, (entity) => {
			if(entity.isPrimary) {
				entity.Type = field.EntityQueryConfiguration.Primary.Type;
			}
			else if(entity.isFriendly) {
				entity.Type = field.EntityQueryConfiguration.Friendly.Type;

				forEach(entity.Entities, (primary) => {
					primary.Type = field.EntityQueryConfiguration.RelationshipType;
				});
			}
		});

		forEach(field.Data, (entity, $index) => {
			field.Data[$index] = fieldEntities[entity.Id];
		});

		this.allEntityConfigs[field.Id] = this.loadConfiguration(field.EntityQueryConfiguration);
		return fieldEntities;
	}

	loadEntityListData(entities, isSelf = false) {
		if(!entities || !entities.length) {
			return [];
		}

		const fieldEntities = {};

		forEach(entities, (entity) => {
			let newEntity = null;

			if(entity.Entity === undefined && entity.Id === undefined) {
				newEntity = undefined;
				return;
			}
			else if(entity.Entity) {
				newEntity = entity.Entity;
				newEntity.isPrimary = true;
			}
			else {
				newEntity = entity;
				newEntity.isFriendly = true;

				if(isSelf === true) {
					newEntity.isSelf = true;
				}
			}

			const subEntities = this.loadEntityListData(newEntity.Entities);

			forEach(subEntities, (sub) => {
				if(fieldEntities[sub.Id]) {
					fieldEntities[sub.Id].friendlyIds.push(newEntity.Id);
					const entityIndex = findIndex(newEntity.Entities, (entity) => entity.Entity.Id === sub.Id);
					newEntity.Entities[entityIndex].Entity = fieldEntities[sub.Id];
				}
				else {
					sub.friendlyIds = [newEntity.Id];
					fieldEntities[sub.Id] = sub;
				}
			});

			this.loadClassificationsIntoEntity(newEntity);
			this.loadIdentifiersIntoEntity(newEntity);

			if(newEntity.Name === undefined) {
				newEntity.Name = newEntity.Code;
			}

			if(!newEntity.Id) {
				return;
			}

			fieldEntities[newEntity.Id] = newEntity;
		});

		return fieldEntities;
	}

	loadClassificationsIntoEntity(entity) {
		if(!entity) {
			return;
		}

		if(!!entity.Classifications) {
			forEach(entity.Classifications, (classification, $index) => {
				entity.Classifications[$index] = this.classifications[classification.Code];
			});
		}

		if(!!entity.Classification) {
			entity.Classification = this.classifications[entity.Classification.Code];
		}
	}

	loadIdentifiersIntoEntity(entity) {
		if(!entity) {
			return;
		}

		if(!!entity.Identifiers) {
			forEach(entity.Identifiers, (identifier, $index) => {
				// entity.Identifiers[$index].Type.Code = identifier.Type.Code;
				if(!!IDENTIFIER_TYPES[identifier.Type.Code]) {
					entity.Identifiers[$index].Type.Name = IDENTIFIER_TYPES[identifier.Type.Code].name;
				}
				else {
					entity.Identifiers[$index].Type.Name = "NO NAME DEFINED";
				}
			});
		}
	}

	loadConfiguration(config) {
		if(config.Friendly) {
			if(config.Friendly.IncludeClassifications) {
				config.Friendly.IncludeClassifications = config.Friendly.IncludeClassifications.map((c) => {
					return this.classifications[c];
				});
			}

			if(config.Friendly.IncludeIdentifiers) {
				config.Friendly.IncludeIdentifiers = config.Friendly.IncludeIdentifiers.map((i) => {
					return IDENTIFIER_TYPES[i];
				});
			}
		}

		if(config.Primary) {
			if(config.Primary.IncludeClassifications) {
				config.Primary.IncludeClassifications = config.Primary.IncludeClassifications.map((c) => {
					return this.classifications[c];
				});
			}

			if(config.Primary.IncludeIdentifiers) {
				config.Primary.IncludeIdentifiers = config.Primary.IncludeIdentifiers.map((i) => {
					return IDENTIFIER_TYPES[i];
				});
			}
		}

		return config;
	}
}

export default angular
	.module("vesl.services.entity-manager-service", [])
	.service("entityManager", EntityManager).name;
